import { Injectable } from '@angular/core';
import { OrganizationSetting } from '@app/orgs/services/org-settings.model';
import { OrgSettingsService } from '@app/orgs/services/org-settings.service';
import { AuthService } from '@app/shared/services/auth.service';
import { ModalService } from '@app/shared/services/modal.service';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DataPrivacyModalComponent } from './components/data-privacy-modal.component';

@Injectable({
  providedIn: 'root',
})
export class DataPrivacyService {
  private readonly settingName: OrganizationSetting =
    'EnforceDataPrivacyAcceptance';

  constructor(
    private authService: AuthService,
    private modalService: ModalService,
    private orgSettingsService: OrgSettingsService
  ) {}

  private get orgId(): number {
    return this.authService.authUser?.defaultOrgId;
  }

  private get isOrgUser(): boolean {
    return this.authService.isOrgUser;
  }

  private get showDataPrivacyAcceptance(): boolean {
    return this.authService.authUser?.showDataPrivacyAcceptance;
  }

  public getDataPrivacyModal() {
    if (this.showDataPrivacyAcceptance) {
      if (this.isOrgUser) {
        return this.orgSettingsService
          .getSetting(this.orgId, this.settingName)
          .subscribe(({ enabled, value }) => {
            if (enabled && value) {
              this.showModal();
            }
          });
      }
      this.showModal();
    }
  }

  private showModal(): void {
    this.getDataPrivacyDisclaimer()
      .pipe(
        switchMap((privacyMessage) =>
          this.modalService.show(DataPrivacyModalComponent, {
            // don't let the user close the modal without accepting the privacy policy
            backdropClickClose: false,
            keyboardEscClose: false,
            inputs: {
              privacyMessage,
            },
          })
        ),
        switchMap(() => this.setDataPrivacy())
      )
      .subscribe();
  }

  // Set privacy acceptance & refresh authenticated user
  private setDataPrivacy(): Observable<void> {
    return this.orgSettingsService
      .setSetting(this.orgId, this.settingName)
      .pipe(switchMap(() => this.authService.fetchAuthenticatedUser(true)));
  }

  private getDataPrivacyDisclaimer(): Observable<string> {
    return this.orgSettingsService.getDataPrivacyDisclaimer(
      this.orgId,
      this.settingName
    );
  }
}
